/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import { withAppManager } from '@ziynx/components/utilities';
import type { TextType } from '@ziynx/flow-types/typography';
import type { IconType } from '@ziynx/flow-types/icons';
import type { PopoverType } from '@ziynx/flow-types/globals';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import Colors from '@ziynx/theme/colors';
import { Body } from '@ziynx/components/typography';
import { Popover } from '@ziynx/components/popovers';
import Icon from '@ziynx/components/icons';
import CardBase from './CardBase';

type StatusType = {
  pending?: boolean,
  popoverPosition?: 'bottomRight' | 'bottomLeft' | 'topRight' | 'topLeft',
  text?: string,
  color?: string,
  iconName?: string,
  iconSize?: 'veryMini' | 'mini' | 'veryTiny' | 'tiny' | 'extraSmall' | 'small' | 'medium' | 'large',
}

export type Props = $ReadOnly<{|
    /**
     * Title prop ex: {text: 'Gabriel Pires', color: Colors.PrimaryBlue}
     */
    title?: TextType,
    /**
     * SubTitle prop ex: {text: 'gabriel.pires@capitalone.com'}
     */
    subtitle?: TextType,
    /**
     * Body prop ex: {text: 'Manager'}
     */
    body?: TextType,
    /**
     * Icon for onClick/options
     */
    icon?: IconType,
    /**
     * Popover props
     */
    popover?: PopoverType,
    /**
     * Status is text or an icon depending on size
     */
    status?: StatusType,
    /**
     * If set to true, card width will not respond to breakpoint and remain fixed.
     */
    fixedWidth?: boolean,
    /**
     * If fixedWidth is true, will scale to set size
     */
    size?: 'extraSmall' | 'small' | 'medium' | 'large',

|}>;

const findWidth = (size, fixedWidth) => {
  if (!fixedWidth && !size) return '100%';

  switch (size) {
  case 'extraSmall':
    return '288px';
  case 'small':
    return '382px';
  case 'medium':
    return '520px';
  case 'large':
    return '688px';
  default:
    return '100%';
  }
};

const StyledCardBase = styled(CardBase)`
    box-sizing: border-box;
    padding: ${({ stacked }) => (!stacked ? '16px 32px' : '16px')};
    flex-direction: ${({ stacked }) => (!stacked ? 'row' : 'column')};
    ${({ stacked }) => !stacked
    && 'align-items: center; justify-content: space-between'};
    max-width: 688px;
    min-width: 288px;
`;

const StyledBody = styled(Body)`
    white-space: nowrap;
    overflow: hidden; 
    text-overflow: ellipsis;
    padding: ${({ padding }) => (padding || '0')};
    ${({ paddingBottom }) => paddingBottom && `padding-bottom: ${paddingBottom}`};
    ${({ width }) => width && `width: ${width};`}
`;

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 4px;
`;

const LargeTitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const IconWrapper = styled.div`
    margin-left: ${({ size }) => (size === 'large' ? '24px' : '16px')};
    cursor: pointer;
`;

const StyledStatusBody = styled(Body)`
  text-align: center;
`;

const BodyWrapper = styled.div`
    display: flex;
    justify-content: ${({ isStacked }) => (isStacked ? 'flex-start' : 'flex-end')};
    align-items: center;
    box-sizing: border-box;
    min-width: 80px;
`;

const StyledDot = styled.div`
    padding: 0px 8px;
    color: ${Colors.Gray};
`;

const CardTable = (props: ManagedProps<Props>): React$Node => {
  const {
    title,
    subtitle,
    body,
    icon,
    popover,
    status,
    fixedWidth,
    size,
    viewport,
    ...rest
  } = props;

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  let stacked = true;
  if (size === 'large' && fixedWidth) {
    stacked = false;
  } else if (!isMobile) {
    stacked = false;
  }

  const CARD_WIDTH = findWidth(size, fixedWidth);

  if (!stacked) {
    return (
      <StyledCardBase
        stacked={stacked}
        viewport={viewport}
        fixedWidth={fixedWidth}
        width={CARD_WIDTH}
        {...rest}
      >
        <LargeTitleWrapper>
          <StyledBody
            width="196px"
            medium
            color={title?.color || Colors.GrayVeryDark}
            paddingBottom="4px"
          >
            {title?.text}
          </StyledBody>
          <StyledBody
            small
            color={body?.color || Colors.Gray}
          >
            {body?.text}
          </StyledBody>
        </LargeTitleWrapper>
        <StyledBody
          width="100%"
          medium
          color={subtitle?.color || Colors.GrayVeryDark}
          padding="0px 24px"
        >
          {subtitle?.text}
        </StyledBody>
        <BodyWrapper>
          {status?.pending
            && (
              <Popover
                triggerType="hover"
                trigger={(
                  <IconWrapper>
                    <Icon
                      {...status}
                      name={status?.iconName || 'invite-pending'}
                      color={status?.color || Colors.Gray}
                      size={status?.iconSize || 'extraSmall'}
                    />
                  </IconWrapper>
                )}
                position={status?.popoverPosition || 'bottomRight'}
              >
                <StyledStatusBody medium color={Colors.GrayDark}>
                  {status?.text}
                </StyledStatusBody>
              </Popover>
            )}
          {popover && (
            <Popover
              trigger={(
                <IconWrapper
                  size="large"
                >
                  <Icon
                    size={icon?.size || 'veryTiny'}
                    color={icon?.color || Colors.Gray}
                    name={icon?.name || 'more'}
                  />
                </IconWrapper>
              )}
              position={popover?.position || 'bottomRight'}
              links={popover?.links}
            >
              {popover?.children}
            </Popover>
          )}
        </BodyWrapper>
      </StyledCardBase>
    );
  }
  return (
    <StyledCardBase
      viewport={viewport}
      stacked={stacked}
      fixedWidth={fixedWidth}
      width={CARD_WIDTH}
      {...rest}
    >
      <TitleWrapper>
        <StyledBody
          color={title?.color || Colors.GrayVeryDark}
          medium
        >
          {title?.text}
        </StyledBody>
        {popover && (
          <Popover
            trigger={(
              <IconWrapper size={size}>
                <Icon
                  size={icon?.size || 'veryTiny'}
                  color={icon?.color || Colors.Gray}
                  name={icon?.name || 'more'}
                />
              </IconWrapper>
            )}
            position={popover?.position || 'bottomRight'}
            links={popover?.links}
          >
            {popover?.children}
          </Popover>
        )}
      </TitleWrapper>
      <StyledBody
        color={subtitle?.color || Colors.GrayVeryDark}
        paddingBottom="8px"
        small
      >
        {subtitle?.text}
      </StyledBody>
      <BodyWrapper isStacked>
        <StyledBody
          color={body?.color || Colors.Gray}
          small
        >
          {body?.text}
        </StyledBody>
        {status?.pending && status?.text && <StyledDot>&#183;</StyledDot>}
        <StyledBody
          color={status?.color || Colors.Gray}
          small
        >
          {status?.text}
        </StyledBody>
      </BodyWrapper>
    </StyledCardBase>
  );
};

export default (withAppManager(CardTable): React$ComponentType<ManagedProps<Props>>);
